import { useMemo } from 'react';
import { useAuthContext } from '@emfood/shared';
import Iconify from '@emfood/shared/src/components/iconify';
import { Chip } from '@mui/material';

import { paths } from 'src/routes/paths';
import { PERMISSIONS } from 'src/api/roles-config';

import SvgColor from 'src/components/svg-color';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  favourite: icon('favourites-icon'),
  orderHistory: icon('orders-icon'),
  products: icon('products-icon'),
  issueLog: <Iconify icon="solar:flag-2-bold" />,
  news: <Iconify icon="material-symbols:news" />,
  stories: <Iconify icon="material-symbols:book-ribbon" />,
  seasonalCalendar: icon('seasonal_calendar_icon'),
  reporting: <Iconify icon="material-symbols:bar-chart-4-bars" />,
  finance: <Iconify icon="solar:wallet-bold" />,
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { permissions } = useAuthContext();

  const data = useMemo(() => {
    const navData = [
      // OVERVIEW
      // ----------------------------------------------------------------------
      // {
      //   subheader: '',
      //   items: [{ title: 'Dashboard', path: paths.dashboard.root, icon: ICONS.dashboard }],
      // },
      {
        subheader: 'Products',
        permissions: [PERMISSIONS.BROWSE_PRODUCTS],
        items: [
          {
            title: 'Products',
            path: paths.dashboard.products.root,
            icon: ICONS.products,
          },
        ],
      },
      {
        subheader: 'Favourites',
        permissions: [PERMISSIONS.FAVOURITES_READ],
        items: [
          {
            title: 'Favourites',
            path: paths.dashboard.favourites.root,
            icon: ICONS.favourite,
          },
        ],
      },
      {
        subheader: 'Orders',
        permissions: [PERMISSIONS.ORDERS_READ],
        icon: ICONS.orderHistory,
        path: paths.dashboard.orders.root,
        items: [
          {
            title: 'Orders',
            path: paths.dashboard.orders.root,
            icon: ICONS.orderHistory,
          },
        ],
      },
      {
        subheader: 'Issue Log',
        permissions: [PERMISSIONS.ORDERS_READ_ISSUES],
        icon: ICONS.issueLog,
        path: paths.dashboard.issues.root,
        items: [
          {
            title: 'Issue Log',
            path: paths.dashboard.issues.root,
            icon: ICONS.issueLog,
          },
        ],
      },

      {
        subheader: 'Seasons',
        permissions: [PERMISSIONS.BUYER_SITES_READ_SEASONS],
        items: [
          {
            title: 'Seasons',
            path: paths.dashboard.seasonalCalendar.root,
            icon: ICONS.seasonalCalendar,
          },
        ],
      },
      {
        subheader: 'News',
        permissions: [PERMISSIONS.NEWS_READ],
        icon: ICONS.news,
        path: paths.dashboard.news,
        items: [
          {
            title: 'News',
            path: paths.dashboard.news.root,
            icon: ICONS.news,
          },
        ],
      },
      {
        subheader: 'Stories',
        permissions: [PERMISSIONS.STORIES_READ],
        icon: ICONS.stories,
        path: paths.dashboard.stories,
        items: [
          {
            title: 'Stories',
            path: paths.dashboard.stories.root,
            icon: ICONS.stories,
            info: (
              <Chip
                label="Beta"
                size="small"
                variant="outlined"
                color="warning"
                sx={{ fontSize: '0.6rem', height: '16px' }}
              />
            ),
          },
        ],
      },
      {
        subheader: 'Reporting',
        items: [
          {
            title: 'Reporting',
            path: paths.dashboard.reporting.root,
            icon: ICONS.reporting,
          },
        ],
      },
      {
        subheader: 'Finance',
        permissions: [PERMISSIONS.FINANCE_READ],
        icon: ICONS.finance,
        path: paths.dashboard.finance.root,
        items: [
          {
            title: 'Finance',
            path: paths.dashboard.finance.root,
            icon: ICONS.finance,
          },
        ],
      },
    ];

    const filterNavData = (config: any[]) =>
      config.filter((item) => {
        if (
          item.permissions &&
          !item.permissions.some((permission: string) => permissions?.includes(permission))
        ) {
          return false;
        }
        if (item.children) {
          item.children = filterNavData(item.children);
        } else if (item.items) {
          item.items = filterNavData(item.items);
        }

        return true;
      });
    const result = filterNavData(navData);

    return result;
  }, [permissions]);

  return data;
}
